import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import { GetServicesByVendor, UpdateService } from '../../api/api';

// "My Work" — what a service owner (caterer, event manager, DJ …) sees after
// signing in with his own mobile: every engagement assigned to him, in day
// order, with the venue, the count to serve and what he has been paid.
// No event needs to be selected: it lists across all events he works on.
export function useMyWork() {
  const { user } = useAuth();
  const toast = useToast();
  const me = user?.UserId;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(() => {
    if (!me) return;
    setLoading(true);
    GetServicesByVendor({ UserId: me })
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => toast('Could not load your work'))
      .finally(() => setLoading(false));
  }, [me, toast]);

  useEffect(() => { load(); }, [load]);

  // Grouped by event, and inside it by day — the order the days run.
  const byEvent = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      const key = String(r.EventId);
      if (!map.has(key)) map.set(key, { EventId: r.EventId, EventName: r.EventName, items: [] });
      map.get(key).items.push(r);
    });
    return Array.from(map.values());
  }, [rows]);

  const totals = useMemo(() => rows.reduce((a, r) => ({
    total: a.total + Number(r.TotalAmount || 0),
    paid: a.paid + Number(r.AdvancePaid || 0),
    balance: a.balance + Number(r.BalanceAmount || 0),
  }), { total: 0, paid: 0, balance: 0 }), [rows]);

  // The owner confirms the count he will serve / the units he will bring.
  const confirmCount = async (row, count) => {
    setSavingId(row.ServiceId);
    try {
      await UpdateService({
        data: { ConfirmedCount: Number(count || 0), Status: row.Status === 'Enquiry' ? 'Confirmed' : row.Status, UpdatedBy: me },
        where: `ServiceId = ${row.ServiceId}`,
      });
      toast('Confirmed');
      load();
    } catch { toast('Could not confirm'); } finally { setSavingId(null); }
  };

  const mapsHref = (row) => {
    const gps = row.GpsLocation;
    if (gps) return /^https?:/i.test(gps) ? gps : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(gps)}`;
    const place = [row.Venue, row.VenueAddress, row.City].filter(Boolean).join(', ');
    return place ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}` : null;
  };

  return { rows, byEvent, totals, loading, savingId, confirmCount, mapsHref, reload: load, user };
}
